import { useFetch } from '@/composables/useFetch';
import { useEnvStore } from '@/stores/envStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useUserStore } from '@/stores/useUserStore';
import type { SubscriptionPlan } from '@/types/api';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export function useSubscription() {
  const userStore = useUserStore();
  const employeeStore = useEmployeeStore();
  const { fetchData } = useFetch();
  const envStore = useEnvStore();
  const { t } = useI18n();

  // Only track loading for subscription-specific operations
  const loading = ref(false);
  const currentPlanId = ref<number | null>(null);

  // Helper to check if user is in trial period
  const isInTrialPeriod = computed(() => {
    const user = userStore.user;
    if (!user?.active_restaurant?.trial_ends_at) return false;

    const trialEndDate = new Date(user.active_restaurant.trial_ends_at);
    const now = new Date();
    return trialEndDate > now;
  });

  // Helper to check if user has active subscription
  const hasActiveSubscription = computed(() => {
    const subscription = userStore.user?.active_restaurant?.subscription;
    return subscription && (['active', 'trialing'].includes(subscription.status) || subscription.is_on_grace_period);
  });

  // Main subscription status check
  const isSubscribed = computed(() => {
    return hasActiveSubscription.value || isInTrialPeriod.value;
  });

  // Employee limits
  const maxEmployeesAllowed = computed(() => {
    const user = userStore.user;

    if (isInTrialPeriod.value && user?.active_restaurant?.subscription === null) {
      // console.log('In trial period, allowing more employees');
      return 1000; // Trial users get more employees
    }

    if (!isSubscribed.value) {
      return 0;
    }

    return user?.active_restaurant?.subscription?.plan?.features?.employees_included ?? 0;
  });

  const maxAdminAccountsAllowed = computed(() => {
    const user = userStore.user;

    if (isInTrialPeriod.value) {
      return 5; // Trial users get more admin accounts
    }
    if (!isSubscribed.value) {
      return 0;
    }
    return user?.active_restaurant?.subscription?.plan?.features?.admin_accounts ?? 0;
  });

  // Use computed values based on existing store data
  const employeeCount = computed(() => {
    const user = userStore.user;
    if (!user?.active_restaurant) return 0;

    return employeeStore.restaurantUsers.filter((member) => member.id !== user.id && member.role === 'employee').length;
  });

  const adminCount = computed(() => {
    const user = userStore.user;
    if (!user?.active_restaurant) return 0;

    return (
      employeeStore.restaurantUsers.filter((member) => member.id !== user.id && (member.role === 'admin' || member.role === 'manager')).length + 1
    ); // +1 for current user
  });

  // Current employee count (including pending invitations)
  const currentEmployeeCount = computed(() => {
    const user = userStore.user;
    if (!user?.active_restaurant) return 0;

    const employees = employeeStore.restaurantUsers.filter(
      (member) => member.registration_completed && member.id !== user.id && member.role === 'employee',
    );

    const pendingUsers = employeeStore.restaurantUsers.filter((member) => !member.registration_completed);

    return employees.length + pendingUsers.length;
  });

  // Can add employee check
  const canAddEmployee = computed(() => {
    if (!isSubscribed.value) return false;
    return currentEmployeeCount.value < maxEmployeesAllowed.value;
  });

  // Feature access checks
  const hasFeature = (featureName: string, minValue: unknown = true) => {
    const user = userStore.user;

    if (isInTrialPeriod.value) {
      return true;
    }

    const features = user?.active_restaurant?.subscription?.plan?.features;
    if (!features || !(featureName in features)) return false;

    const value = features[featureName];
    if (typeof minValue === 'boolean') return !!value === minValue;
    if (typeof minValue === 'number') return typeof value === 'number' && value >= minValue;
    return value === minValue;
  };

  // Specific feature checks
  const canAccessPremiumFeatures = computed(() => hasFeature('premium_features'));
  const canAccessAdvancedReporting = computed(() => hasFeature('advanced_reporting'));
  const canAccessMultipleLocations = computed(() => hasFeature('multiple_locations'));

  // Credit limits
  const maxCreditsAllowed = computed(() => {
    const user = userStore.user;

    if (isInTrialPeriod.value) {
      return 1000;
    }

    if (!isSubscribed.value) {
      return 0;
    }

    return user?.active_restaurant?.subscription?.plan?.features?.ai_generations_per_month ?? 0;
  });

  // Optimized functions that use the store
  const ensureTeamDataLoaded = async () => {
    // This will only fetch if data doesn't exist - very fast if already loaded
    await employeeStore.fetchRestaurantUsersIfNeeded();
  };

  const fetchCurrentPlan = async () => {
    if (currentPlanId.value !== null) {
      return; // Already fetched
    }

    loading.value = true;
    try {
      const { data } = await fetchData(`${envStore.apiUrl}/subscription-details`);
      if (data?.plan?.id) {
        currentPlanId.value = data.plan.id;
      }
    } catch (error) {
      console.error('Error fetching current plan:', error);
    } finally {
      loading.value = false;
    }
  };

  const canDowngradeToPlan = (targetPlan: SubscriptionPlan) => {
    if (!currentPlanId.value) {
      // console.log('No current plan ID, downgrade is possible.');
      return { canDowngrade: true, reasons: [], impact: {} };
    }

    const reasons: string[] = [];
    const impact: { employees_to_remove?: number; admins_to_convert?: number } = {};

    // Calculate adjustments needed (following backend logic)
    const targetEmployeeLimit = targetPlan.features.employees_included || 0;
    const targetAdminLimit = targetPlan.features.admin_accounts || 1;

    const adminsToConvert = Math.max(0, adminCount.value - targetAdminLimit);
    const finalEmployeeCount = employeeCount.value + adminsToConvert;
    const employeesToRemove = Math.max(0, finalEmployeeCount - targetEmployeeLimit);

    // Check if adjustments are needed for business plan downgrades
    if (targetPlan.category === 'business') {
      // Check if we need to remove employees
      if (employeesToRemove > 0) {
        reasons.push(
          t('subscription.downgrade.tooManyEmployees', {
            current: finalEmployeeCount,
            limit: targetEmployeeLimit,
          }),
        );
        impact.employees_to_remove = employeesToRemove;
      }

      // Check if we need to convert admins
      if (adminsToConvert > 0) {
        reasons.push(
          t('subscription.downgrade.tooManyAdmins', {
            current: adminCount.value,
            limit: targetAdminLimit,
          }),
        );
        impact.admins_to_convert = adminsToConvert;
      }
    }

    return {
      canDowngrade: reasons.length === 0,
      reasons,
      impact,
    };
  };

  const isDowngrade = computed(() => (targetPlan: SubscriptionPlan) => {
    if (!currentPlanId.value) return false;
    return targetPlan.id !== currentPlanId.value;
  });

  // Force refresh function for when you need fresh data
  const refreshTeamData = async () => {
    await employeeStore.fetchRestaurantUsers(true);
  };

  const resetCurrentPlan = () => {
    currentPlanId.value = null;
  };

  return {
    // Status checks
    isSubscribed,
    isInTrialPeriod,
    hasActiveSubscription,

    // Employee management
    maxEmployeesAllowed,
    currentEmployeeCount,
    canAddEmployee,
    maxAdminAccountsAllowed,

    // Feature access
    hasFeature,
    canAccessPremiumFeatures,
    canAccessAdvancedReporting,
    canAccessMultipleLocations,

    // Limits
    maxCreditsAllowed,

    // Team data (reactive from store)
    employeeCount,
    adminCount,
    currentPlanId: computed(() => currentPlanId.value),
    loading: computed(() => loading.value),

    // Optimized functions
    ensureTeamDataLoaded,
    fetchCurrentPlan,
    canDowngradeToPlan,
    isDowngrade,

    // Utility functions
    refreshTeamData,
    resetCurrentPlan,
  };
}
